'use client'

import { CrossIcon } from '@repo/ui/CrossIcon'
import { DateSeparator } from '@repo/ui/DateSeparator'
import { Drawer } from '@repo/ui/Drawer'
import { EmptyState } from '@repo/ui/EmptyState'
import { LargeNotesIcon } from '@repo/ui/LargeNotesIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { NoteItem } from '@repo/ui/NoteItem'
import { NotesForm } from '@repo/ui/NotesForm'
import { PrintIcon } from '@repo/ui/PrintIcon'
import { useClientStore } from '@repo/store/useClientStore'
import { useEffect, useState, useCallback } from 'react'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const NotesDrawer = ({ level = 'Matter' }) => {
  const { matterNotes, currentClientMatterNo, fetchMatterNotes, matterNotesIsOpen } = useMattersStore()
  const { allowedActions } = useMenuItemsStore()
  const allowViewNotes = allowedActions?.viewNotes?.allowed || false
  const allowEditNotes = allowedActions?.editNotes?.allowed || false
  const { currentClientNo } = useClientStore()
  const [entityId, setEntityId] = useState(null)
  const [notesForThisEntity, setNotesForThisEntity] = useState([])
  const [dates, setDates] = useState([])
  const [isCreatingNote, setIsCreatingNote] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const { path } = useUrlStore()

  useEffect(() => {
    let entityId = null

    if (level === 'Matter' && currentClientMatterNo) {
      entityId = currentClientMatterNo
    } else if (level === 'Client' && currentClientNo) {
      entityId = `${currentClientNo}-0`
    }

    setEntityId(entityId)
  }, [currentClientMatterNo, currentClientNo, level])

  const fetchNotes = useCallback(async () => {
    if (!allowViewNotes) return

    if (entityId && level) {
      setIsFetching(true)
      await fetchMatterNotes({ clientMatterNo: entityId })
      setIsFetching(false)
    }
  }, [entityId, fetchMatterNotes, level, allowViewNotes])

  useEffect(() => {
    fetchNotes()
  }, [fetchNotes, path])

  useEffect(() => {
    if (entityId && matterNotes) {
      setNotesForThisEntity(matterNotes[entityId] || [])
    }
  }, [entityId, matterNotes])

  useEffect(() => {
    if (matterNotesIsOpen) fetchNotes()
    else setIsCreatingNote(false)
  }, [matterNotesIsOpen, fetchNotes])

  useEffect(() => {
    if (notesForThisEntity) {
      const dates = notesForThisEntity
        .map(({ formattedDate }) => formattedDate)
        .filter((date, index, self) => self.indexOf(date) === index) // de-dupe
      setDates(dates)
    }
  }, [notesForThisEntity])

  const toggleMatterNotesDrawer = (bool) => {
    useMattersStore.setState({ matterNotesIsOpen: bool })
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div data-testid="NotesDrawer">
      <Drawer
        isOpen={matterNotesIsOpen}
        onClose={() => toggleMatterNotesDrawer(false)}
        sx={{
          '@media print': {
            [`& [data-testid='AddIcon']`]: {
              display: 'none'
            },
            width: '100%',
            height: 'auto',
            position: 'absolute',
            overflow: 'visible',
            boxShadow: 'none'
          }
        }}
        drawerSx={{
          '@media print': {
            width: '100%',
            height: 'auto',
            position: 'static',
            overflow: 'visible'
          }
        }}
        headingNode={
          <MUITypography
            variant="h5"
            sx={{
              mb: 0,
              alignItems: 'center',
              display: 'flex',
              '@media print': {
                backgroundColor: '#ffffff'
              }
            }}
          >
            <LargeNotesIcon sx={{ width: '36px', height: '36px', mr: 3 }} />
            {level} notes: {level === 'Matter' ? currentClientMatterNo : currentClientNo}
          </MUITypography>
        }
      >
        {isFetching ? (
          <LoadingState />
        ) : (
          <MUIStack
            direction="column"
            gap={'2px'}
            alignItems="flex-end"
            sx={{
              padding: '0 20px 20px',
              overflowY: 'auto',
              '@media print': {
                backgroundColor: '#ffffff',
                overflowY: 'visible'
              }
            }}
          >
            {isCreatingNote ? (
              <NotesForm
                entityId={entityId}
                onSubmitComplete={() => setIsCreatingNote(false)}
                onCancel={() => setIsCreatingNote(false)}
              />
            ) : (
              <>
                <MUIStack
                  direction="row"
                  gap={2}
                  sx={{
                    width: '100%',
                    '@media print': {
                      display: 'none'
                    }
                  }}
                  justifyContent={'space-between'}
                  alignItems={'stretch'}
                >
                  <MUIButton variant="outlined" color="primary" onClick={handlePrint} sx={{ m: 0 }}>
                    <PrintIcon sx={{ width: '18px', height: '18px', mr: 2 }} />
                    Print
                  </MUIButton>
                  {allowEditNotes ? (
                    <MUIButton
                      variant="contained"
                      color="primary"
                      onClick={() => setIsCreatingNote(true)}
                      sx={{ width: '140px', m: 0 }}
                    >
                      <CrossIcon
                        sx={{ width: '18px', height: '18px', mr: 2, color: 'white', transform: 'rotate(45deg)' }}
                      />
                      New note
                    </MUIButton>
                  ) : null}
                </MUIStack>

                {dates.length ? (
                  dates.map((formattedDate) => {
                    return (
                      <div key={`notes-${formattedDate}`} style={{ width: '100%' }}>
                        <DateSeparator dateToDisplay={formattedDate} />
                        <MUIStack direction="column" gap={'2px'} sx={{ width: '100%' }}>
                          {notesForThisEntity
                            ?.filter((matterNotes) => matterNotes.formattedDate === formattedDate)
                            .sort((a, b) => {
                              if (a.matterNoteId > b.matterNoteId) return -1
                              return 1
                            })
                            .map(({ notes, matterNoteId, dateEntered }, index) => {
                              return (
                                <NoteItem
                                  key={`${matterNoteId}-${index}`}
                                  entityId={entityId}
                                  matterNoteId={matterNoteId}
                                  notes={notes}
                                  dateEntered={dateEntered}
                                />
                              )
                            })}
                        </MUIStack>
                      </div>
                    )
                  })
                ) : (
                  <EmptyState
                    sx={{ mt: 10 }}
                    heading="No notes to show"
                    text="Add a note to get started"
                    icon={<LargeNotesIcon sx={{ marginBottom: 10, width: '60px', height: '60px' }} />}
                  />
                )}
              </>
            )}
          </MUIStack>
        )}
      </Drawer>
    </div>
  )
}

NotesDrawer.propTypes = {
  level: PropTypes.string
}
